import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export interface NotificationPreferences {
  enable_system: boolean
  enable_team: boolean
  enable_resource: boolean
  email_enabled: boolean
  push_enabled: boolean
  dnd_enabled: boolean
  dnd_start: string | null
  dnd_end: string | null
  timezone: string
}

function getDetectedTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz && tz.trim()) return tz
  } catch {
    // ignore
  }
  return 'UTC'
}

function getDefaultPreferences(): NotificationPreferences {
  return {
    enable_system: true,
    enable_team: true,
    enable_resource: true,
    email_enabled: false,
    push_enabled: false,
    dnd_enabled: false,
    dnd_start: null,
    dnd_end: null,
    timezone: getDetectedTimezone(),
  }
}

export function useNotificationPreferences(userId: string | null | undefined) {
  const queryClient = useQueryClient()

  const preferencesQuery = useQuery({
    queryKey: ['notification_preferences', userId],
    queryFn: async () => {
      if (!userId) return null

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('enable_system, enable_team, enable_resource, email_enabled, push_enabled, dnd_enabled, dnd_start, dnd_end, timezone')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) throw error

      const defaults = getDefaultPreferences()
      if (!data) return defaults

      return {
        enable_system: data.enable_system ?? defaults.enable_system,
        enable_team: data.enable_team ?? defaults.enable_team,
        enable_resource: data.enable_resource ?? defaults.enable_resource,
        email_enabled: data.email_enabled ?? defaults.email_enabled,
        push_enabled: data.push_enabled ?? defaults.push_enabled,
        dnd_enabled: data.dnd_enabled ?? defaults.dnd_enabled,
        dnd_start: data.dnd_start ?? defaults.dnd_start,
        dnd_end: data.dnd_end ?? defaults.dnd_end,
        timezone: (data.timezone && data.timezone.trim()) ? data.timezone : defaults.timezone,
      } satisfies NotificationPreferences
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  })

  const saveMutation = useMutation({
    mutationKey: ['notification_preferences', userId, 'save'],
    mutationFn: async (next: NotificationPreferences) => {
      if (!userId) throw new Error('Missing userId')

      const payload = {
        user_id: userId,
        enable_system: !!next.enable_system,
        enable_team: !!next.enable_team,
        enable_resource: !!next.enable_resource,
        email_enabled: !!next.email_enabled,
        push_enabled: !!next.push_enabled,
        dnd_enabled: !!next.dnd_enabled,
        dnd_start: next.dnd_start,
        dnd_end: next.dnd_end,
        timezone: next.timezone || getDetectedTimezone(),
        updated_at: new Date().toISOString(),
      }

      const { data, error } = await supabase
        .from('notification_preferences')
        .upsert(payload, { onConflict: 'user_id' })
        .select('enable_system, enable_team, enable_resource, email_enabled, push_enabled, dnd_enabled, dnd_start, dnd_end, timezone')
        .single()

      if (error) throw error

      return {
        enable_system: data.enable_system,
        enable_team: data.enable_team,
        enable_resource: data.enable_resource,
        email_enabled: data.email_enabled,
        push_enabled: data.push_enabled,
        dnd_enabled: data.dnd_enabled,
        dnd_start: data.dnd_start,
        dnd_end: data.dnd_end,
        timezone: data.timezone || getDetectedTimezone(),
      } satisfies NotificationPreferences
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['notification_preferences', userId], next)
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
      queryClient.invalidateQueries({ queryKey: ['notifications', userId, 'unreadCount'] })
    },
  })

  return {
    preferences: preferencesQuery.data ?? (userId ? getDefaultPreferences() : null),
    isLoading: preferencesQuery.isLoading,
    error: preferencesQuery.error ?? saveMutation.error,
    savePreferences: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  }
}
